// Pure view-model helpers for the PDP portfolio position popover. Inputs are already normalized by
// the /portfolio/ normalizers, so this file stays free of DOM, network and formatting concerns.

export const RECENT_TRANSACTION_LIMIT = 3;
export const SELECTED_PORTFOLIO_KEY = 'arz-portfolio-selected';

function portfolioKey(portfolio) {
  return String(portfolio?.id ?? portfolio?.portfolio_id ?? portfolio?.uuid ?? '');
}

// Same preference order as /portfolio/: the stored selection, then the default, then the first one.
export function choosePortfolio(portfolios, storedId = '') {
  if (!Array.isArray(portfolios) || !portfolios.length) return null;
  return portfolios.find((item) => storedId && portfolioKey(item) === String(storedId))
    || portfolios.find((item) => item?.is_default)
    || portfolios[0];
}

function finite(value) {
  return value !== null && value !== undefined && value !== '' && Number.isFinite(Number(value)) ? Number(value) : null;
}

// Share of the portfolio's positive value; only meaningful when both sides are known and positive.
export function allocationPercent(holding, detail, currency) {
  const key = currency === 'irt' ? 'valueIrt' : 'valueUsd';
  const part = finite(holding?.[key]);
  const total = finite(detail?.[key]);
  if (part === null || total === null || part <= 0 || total <= 0) return null;
  return Math.min(100, (part / total) * 100);
}

export function findHolding(detail, assetId) {
  if (!detail || !Array.isArray(detail.assets)) return null;
  return detail.assets.find((item) => String(item.id) === String(assetId)) || null;
}

export function resolvePosition({ signedIn, portfolios, detail, assetId, transactions = null, transactionsFailed = false, currency = 'usd' }) {
  if (!signedIn) return { kind: 'guest' };
  if (!Array.isArray(portfolios)) return { kind: 'error' };
  if (!portfolios.length) return { kind: 'no-portfolio' };
  // An empty detail object is incomplete service data, not an empty portfolio (portfolio.md).
  if (!detail || detail.incomplete) return { kind: 'error' };

  const holding = findHolding(detail, assetId);
  const history = Array.isArray(transactions) ? transactions : [];
  if (!holding && !history.length) return { kind: 'not-held' };

  const quantity = finite(holding?.quantity);
  const recent = history.slice(0, RECENT_TRANSACTION_LIMIT);
  const base = {
    holding: holding || { id: String(assetId), quantity: 0 },
    recent,
    transactionCount: Array.isArray(transactions) ? history.length : null,
    transactionsFailed: Boolean(transactionsFailed),
    allocation: allocationPercent(holding, detail, currency),
  };
  // Zero after selling everything keeps the realized P&L visible instead of reading as "not held".
  if (quantity === 0) return { kind: 'closed', ...base, allocation: null };
  return { kind: 'held', ...base };
}

export function pnlTone(value) {
  const number = finite(value);
  if (number === null || number === 0) return 'neutral';
  return number > 0 ? 'positive' : 'negative';
}

function withQuery(base, params) {
  const query = new URLSearchParams(Object.entries(params).filter(([, value]) => value !== '' && value !== null && value !== undefined));
  const text = query.toString();
  return text ? `${base}${base.includes('?') ? '&' : '?'}${text}` : base;
}

// Links never carry the portfolio id; the selection travels through the shared localStorage key.
export function portfolioLinks(portfolioUrl, asset) {
  const base = String(portfolioUrl || '/portfolio/');
  const id = String(asset?.id || '');
  return {
    home: base,
    add: withQuery(base, { action: 'add', asset: id, name: asset?.name || '', symbol: asset?.symbol || '' }),
    transactions: withQuery(base, { view: 'transactions', asset: id }),
  };
}
